/** Public recovery page. The token is captured before scripts and kept in RAM. */
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';

import './auth.css';
import { AlphaMark } from '@/components/app/Sidebar';
import { ThemeToggle } from '@/components/app/ThemeToggle';
import { Button, Callout, Field, IconButton, Input } from '@/components/ui';
import { ApiError, request } from '@/lib/api';
import { ROUTES } from '@/lib/constants';
import { ResetAccessDialog } from './ResetAccessDialog';

declare global { interface Window { __afPasswordResetToken?: string } }
const FIELD_FRAME = 'bg-[var(--surface-2)] border-[color:var(--af-field-line)] hover:border-[color:var(--af-field-line-strong)] focus-within:shadow-none focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-[color:var(--focus)]';

export function ResetPasswordPage() {
  // Initializers are non-destructive: React StrictMode may call them twice.
  // https://react.dev/reference/react/useState#my-initializer-or-updater-function-runs-twice
  const [token, setToken] = useState(() => window.__afPasswordResetToken ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [pending, setPending] = useState(false);
  const [complete, setComplete] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const statusRef = useRef<HTMLHeadingElement>(null);
  const requestRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    delete window.__afPasswordResetToken;
    document.title = 'Reset your password · AlphaForm CRM';
  }, []);
  useEffect(() => { if (complete || !token) statusRef.current?.focus(); }, [complete, token]);

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = password.length >= 12 && password.length <= 200 && confirm === password && !pending;
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setPending(true);
    setError(null);
    try {
      await request('/auth/reset-password', { method: 'POST', body: { token, password } });
      setPassword('');
      setConfirm('');
      setToken('');
      setComplete(true);
    } catch (failure) {
      if (ApiError.is(failure) && failure.status === 422) {
        setToken('');
        setError('This reset link is invalid or has expired. Request a new link.');
      } else {
        setError(ApiError.is(failure) && failure.status === 429
          ? 'Too many attempts. Wait a little before trying again.'
          : 'We could not change your password. Check your connection and try again.');
      }
    } finally { setPending(false); }
  }
  return (
    <div className="af-auth flex h-full w-full flex-col bg-[var(--surface-1)]">
      <header className="flex h-[var(--h-topbar)] shrink-0 items-center px-6">
        <div className="mx-auto flex w-full max-w-[var(--w-auth-form)] items-center justify-between gap-3">
          <span className="flex items-center gap-2"><AlphaMark size={28} /><span className="flex flex-col leading-none"><span className="text-base font-semibold text-primary">AlphaForm Group</span><span className="eyebrow mt-1">CRM</span></span></span>
          <ThemeToggle size="sm" />
        </div>
      </header>
      <main className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto px-6">
        <div className="w-full max-w-[var(--w-auth-form)] py-8">
          {complete ? (
            <section aria-labelledby="af-reset-complete">
              <h1 id="af-reset-complete" ref={statusRef} tabIndex={-1} className="text-2xl font-semibold text-primary">Your password is ready</h1>
              <p role="status" className="mt-3 text-sm text-secondary">Sign in with your new password. Your other sessions have been signed out.</p>
              <Button className="mt-6 w-full" onClick={() => window.location.assign(ROUTES.login)}>Go to sign in</Button>
            </section>
          ) : !token ? (
            <section>
              <h1 ref={statusRef} tabIndex={-1} className="text-2xl font-semibold text-primary">Request a new reset link</h1>
              <p className="mt-3 text-sm text-secondary">{error ?? 'This link is missing or incomplete. Reset links work once and expire after 15 minutes.'}</p>
              <Button ref={requestRef} className="mt-6 w-full" onClick={() => setRecovering(true)}>Request a new link</Button>
              <Button variant="ghost" className="mt-3 w-full" onClick={() => window.location.assign(ROUTES.login)}>Back to sign in</Button>
            </section>
          ) : (
            <form onSubmit={submit} noValidate>
              <h1 className="text-2xl font-semibold text-primary">Choose a new password</h1>
              <p className="mt-3 text-sm text-secondary">Use 12 to 200 characters. You will sign in with this password after saving it.</p>
              {error && <Callout variant="danger" className="mt-4">{error}</Callout>}
              <Field label="New password" htmlFor="af-reset-password" className="mt-6" error={tooShort ? 'Use at least 12 characters.' : undefined}>
                {({ id, describedBy, invalid }) => <div className="relative">
                  <Input id={id} type={revealed ? 'text' : 'password'} name="new-password" autoComplete="new-password" required autoFocus minLength={12} maxLength={200} size="lg" iconLeft={<Lock />} containerClassName={`${FIELD_FRAME} pr-8`} value={password} invalid={invalid} aria-describedby={describedBy} disabled={pending} onChange={(event) => setPassword(event.target.value)} />
                  <IconButton label={revealed ? 'Hide passwords' : 'Show passwords'} aria-pressed={revealed} aria-controls="af-reset-password af-reset-confirm" variant="ghost" size="sm" className="absolute right-1 top-1/2 -translate-y-1/2" disabled={pending} onClick={() => setRevealed((value) => !value)}>{revealed ? <EyeOff /> : <Eye />}</IconButton>
                </div>}
              </Field>
              <Field label="Confirm password" htmlFor="af-reset-confirm" className="mt-4" error={mismatch ? 'These passwords do not match.' : undefined}>
                {({ id, describedBy, invalid }) => <Input id={id} type={revealed ? 'text' : 'password'} name="confirm-password" autoComplete="new-password" required maxLength={200} size="lg" iconLeft={<Lock />} containerClassName={FIELD_FRAME} value={confirm} invalid={invalid} aria-describedby={describedBy} disabled={pending} onChange={(event) => setConfirm(event.target.value)} />}
              </Field>
              <Button type="submit" className="mt-6 w-full" disabled={!canSubmit} loading={pending} loadingLabel="Saving password…">Save new password</Button>
              <Button variant="ghost" className="mt-3 w-full" disabled={pending} onClick={() => window.location.assign(ROUTES.login)}>Back to sign in</Button>
            </form>
          )}
        </div>
      </main>
      <ResetAccessDialog open={recovering} onOpenChange={setRecovering} onCloseAutoFocus={(event) => { event.preventDefault(); requestRef.current?.focus(); }} />
    </div>
  );
}

export default ResetPasswordPage;
